import * as React from "react";
import {ISelectValue} from "../common/Select.component";
import {Label, Legend, Pie, PieChart, PieSectorShapeProps, Sector, Tooltip} from "recharts";
import {
    calculateColorAndOpacity,
    getTotalNode,
} from "./portfolioUtil";
import {useEffect, useState} from "react";
import {
    getMonthAndYearString,
    parseDate
} from "../../utils/dateUtil";
import {formatCurrency} from "../../utils/numberUtil";
import {IPlanTreeNode} from "../../model/Portfolio.model";
import {buildAssetAllocationTreeByDate} from "./portfolioPlanTreeUtil";

export interface IAssetAllocationDonutChartProps {
    dataNodes: IPlanTreeNode[],
    selectedDate?: ISelectValue,
    key?: string
    titlePrefix: string
}

interface IPieChartData {
    name: string,
    value: number,
    fill: string,
    opacity: number
}

const LABEL_TEXT_X_AXIS = 120;
const LABEL_TEXT_Y_AXIS = 90;
const LABEL_TEXT_SPAN_X_AXIS = 130;
const CHART_X_AXIS = 120;
const CHART_Y_AXIS = 90;
const CHART_INNER_RADIUS = 90;
const CHART_OUTER_RADIUS = 120;

export const AssetAllocationDonutChart: React.FunctionComponent<IAssetAllocationDonutChartProps> = (props) => {
    const monthAndYear = getMonthAndYearString(parseDate(props.selectedDate?.id as string));
    const [nodes, setNodes] = useState<IPieChartData[]>([]);

    useEffect(() => {
        const date = (props.selectedDate?.id as string);
        setNodes(buildAssetAllocationTreeByDate(props.dataNodes, date)
            .map((node, index) => {
                const colorAndOpacity = calculateColorAndOpacity(index);
                return {
                    name: `${node.name}, ${node.marketValuePctOfTotal.toFixed(1)}%`,
                    value: node.marketValue,
                    fill: colorAndOpacity.fill,
                    opacity: colorAndOpacity.opacity,
                };
            })
        );
    }, [props]);

    const renderMarketValue = () => {
        const totalNode = getTotalNode(props.dataNodes, props.selectedDate?.id as string) as IPlanTreeNode | undefined;
        return <div className="asset-allocation-donut-chart__market-value-container">
            {totalNode && <div className="asset-allocation-donut-chart__market-value-text">{props.titlePrefix} Market Value</div>}
            <div className="asset-allocation-donut-chart__market-value">{totalNode === undefined
                ? ""
                : formatCurrency(totalNode!.marketValue, 0)}</div>
        </div>;
    };

    const renderAssetAllocationDonutChart = () => {
        return <div className="asset-allocation-donut-chart__container"
                    data-testid="asset-allocation-donut-chart__container">
            {renderMarketValue()}
            <PieChartComponent nodes={nodes} monthAndYear={monthAndYear}/>
        </div>;
    };

    return renderAssetAllocationDonutChart();
};

interface IPieChartComponentProps {
    nodes: IPieChartData[],
    monthAndYear: string
}

const renderPieSector = (shapeProps: PieSectorShapeProps) => {
    const {payload, opacity, ...sectorProps} = shapeProps;

    return <Sector
        {...sectorProps}
        fill={payload.fill}
        fillOpacity={payload.opacity ?? opacity ?? 1}
        stroke={payload.fill}
    />;
};

const renderCenterLabel = (monthAndYear: string) => {
    const [year, ...monthPartsReversed] = monthAndYear.split(" ").reverse();
    const month = monthPartsReversed.reverse().join(" ");

    return (
        <text x={LABEL_TEXT_X_AXIS} y={LABEL_TEXT_Y_AXIS} textAnchor="middle" dominantBaseline="middle" className="quarter-label">
            <tspan x={LABEL_TEXT_SPAN_X_AXIS} dy="-0.45em">{`${month} `}</tspan>
            <tspan x={LABEL_TEXT_SPAN_X_AXIS} dy="1.1em">{year}</tspan>
        </text>
    );
};

const PieChartComponent: React.FunctionComponent<IPieChartComponentProps> = (props) => {
    const customLegend = (props: any) => {
        const {payload} = props;

        return (
            <div className="donut-chart__legend-container">
                {payload.map((entry: any, index: number) => (
                    <div key={`item-${index}`}>
                        <div className="fa-solid fa-circle donut-chart__legend-dot" style={{
                            color: calculateColorAndOpacity(index).fill,
                            opacity: calculateColorAndOpacity(index).opacity,
                        }}/>
                        <div className="donut-chart__legend-text">{entry.value}</div>
                    </div>
                ))}
            </div>
        );
    };

    const customTooltip = (toolTipProps: any) => {
        if (toolTipProps.active && toolTipProps.payload && toolTipProps.payload.length) {
            return (
                <div className="custom-tooltip">
                    <div className="label">{toolTipProps.payload[0].payload.name.split(",")[0]}</div>
                    <div className="value">{formatCurrency(toolTipProps.payload[0].value, 0)}</div>
                </div>
            );
        }
        return null;
    };

    return <PieChart width={420} height={268}>
        <Pie
            dataKey="value"
            data={props.nodes}
            cx={CHART_X_AXIS}
            cy={CHART_Y_AXIS}
            innerRadius={CHART_INNER_RADIUS}
            outerRadius={CHART_OUTER_RADIUS}
            legendType={"circle"}
            isAnimationActive={false}
            shape={renderPieSector}
        >
            <Label content={() => renderCenterLabel(props.monthAndYear)}/>
        </Pie>
        <Tooltip content={customTooltip} position={{x: -5, y: 205}}/>
        <Legend
            width={438}
            wrapperStyle={{
                top: 10,
                left: 235,
                lineHeight: "30px",
                textAlign: "left",
            }}
            content={customLegend}
        />
    </PieChart>;
};